import {
    Button,
    TextButton,
    Typography,
    useAlert,
    useCalendar
} from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import CheckIcon from "@mui/icons-material/Check"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import EditCalendarIcon from "@mui/icons-material/EditCalendar"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import UploadIcon from "@mui/icons-material/Upload"
import dayjs from "dayjs"
import React from "react"
import { theme } from "../theme"
import { FooterIconButton } from "../components/FooterIconButtonAsLink"
import { ShiftCalendarPageUI } from "./ShiftPage/ShiftCalendarPageUI"
import { useShift } from "@/hooks/useShifts"
import { requestSubmitShifts } from "@/hooks/useShift"
import { displayWeekdayJa } from "@/utils/displayWeekday"
import { Shift, getShiftCurrentTime } from "@/model/shift"
import { useTranslation } from "@/hooks/useTranslation"

const SelectItem = ({
    checked,
    onChange,
    children,
    horizontalPadding = 0,
    ...buttonProps
}: React.ComponentPropsWithoutRef<"button"> & {
    checked?: boolean
    horizontalPadding?: number
}) => {
    return (
        <button
            {...buttonProps}
            css={[
                css`
                    display: grid;
                    grid-template-columns: 1fr auto;
                    gap: 16px;
                    align-items: center;
                    padding: 12px ${horizontalPadding}px;
                    color: inherit;
                    text-align: left;
                `,
                checked &&
                    css`
                        background-color: ${
                            theme.palette.intentions.cleanerGray.light
                        };
                    `
            ]}
        >
            <Typography
                css={css`
                    font-size: 16px;
                `}
            >
                {children}
            </Typography>
            <CheckIcon
                css={
                    !checked &&
                    css`
                        color: ${theme.palette.intentions.cleanerGray.main};
                    `
                }
            />
        </button>
    )
}

interface ShiftSelectContextState {
    ids: string[]
    toggle: (id: string) => void
    set: (ids: string[]) => void
}

const ShiftSelectContext = React.createContext<
    ShiftSelectContextState | undefined
>(undefined)

export const useShiftSelect = () => {
    const context = React.useContext(ShiftSelectContext)
    if (context === undefined) {
        throw new Error("useShiftSelect must be used within a ShiftSelect")
    }

    return context
}

const RenderContent = ({
    initialDate: targetDate,
    shifts,
    renderControls: RenderControls
}: {
    initialDate: dayjs.Dayjs
    shifts?: Shift[]
    renderControls?: () => React.ReactElement
}) => {
    const { ids, toggle, set } = useShiftSelect()
    const { t } = useTranslation()

    return (
        <div
            css={css`
                display: grid;
                gap: 16px;
                padding: 24px 0;
            `}
        >
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 20px;
                `}
            >
                <Typography variant="caption">
                    {t(`${targetDate.format("M月")}のシフト`)}
                </Typography>

                <div
                    css={css`
                        display: flex;
                        gap: 16px;
                    `}
                >
                    <div
                        css={css`
                            display: flex;
                            gap: 8px;
                        `}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => {
                                set([])
                            }}
                        >
                            {t("全て選択解除")}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => {
                                if (shifts) {
                                    set(shifts.map(shift => shift.id))
                                }
                            }}
                        >
                            {t("全て選択")}
                        </Button>
                    </div>

                    {RenderControls && <RenderControls />}
                </div>
            </div>

            <div
                css={css`
                    display: grid;
                    gap: 24px;
                `}
            >
                <div
                    css={css`
                        display: grid;

                        & > *:first-of-type {
                            border-top: 1px solid
                                ${theme.palette.intentions.cleanerGray.light};
                        }
                        & > * {
                            border-bottom: 1px solid
                                ${theme.palette.intentions.cleanerGray.light};
                        }
                    `}
                >
                    {shifts
                        ?.slice()
                        .sort((a, b) =>
                            getShiftCurrentTime(a).start >
                            getShiftCurrentTime(b).end
                                ? 1
                                : -1
                        )
                        .map(shift => (
                            <SelectItem
                                key={shift.id}
                                checked={ids.includes(shift.id)}
                                onClick={() => {
                                    toggle(shift.id)
                                }}
                                horizontalPadding={20}
                            >
                                {dayjs
                                    .unix(getShiftCurrentTime(shift).start)
                                    .format("MM/DD(w)　HH:mm")
                                    .replace(
                                        "(w)",
                                        `(${displayWeekdayJa(
                                            dayjs
                                                .unix(
                                                    getShiftCurrentTime(shift)
                                                        .start
                                                )
                                                .day()
                                        )})`
                                    )}
                                ~{dayjs.unix(shift.cleanerEnd).format("HH:mm")}
                            </SelectItem>
                        ))}
                </div>
            </div>
        </div>
    )
}

const RenderCalendarControls = () => {
    const { t } = useTranslation()
    return (
        <div
            css={css`
                display: grid;
                place-items: center;
                height: 100%;
                background-color: ${theme.palette.intentions.primary.light};
            `}
        >
            <Typography>{t("提出するシフトを選択してください")}</Typography>
        </div>
    )
}

export const SubmitShiftPage = () => {
    const { t } = useTranslation()
    const { state } = useLocation()
    const stateTyped = state as { date?: string } | null
    const targetDate = stateTyped?.date ? dayjs(stateTyped.date) : dayjs()

    const { date, set } = useCalendar({ initialDate: targetDate })

    const { token } = useAuth()
    const { data: shifts } = useShift(
        token,
        date.startOf("month"),
        date.endOf("month")
    )

    const { throwAlert } = useAlert()
    const navigate = useNavigate()

    const [ids, setIds] = useState<string[]>([])

    const confirmBackMessage = t("シフトを提出せず、一覧に戻りますか？")

    const value = useMemo(
        () => ({
            ids,
            toggle: (id: string) => {
                setIds(prev =>
                    prev.includes(id)
                        ? prev.filter(prevId => prevId !== id)
                        : [...prev, id]
                )
            },
            set: (ids: string[]) => {
                setIds(ids)
            }
        }),
        [ids]
    )

    const [fullHeight, setFullHeight] = useState(false)

    return (
        <ShiftSelectContext.Provider value={value}>
            <ShiftCalendarPageUI
                disableScheduledColor
                shouldRenderContentFullHeight={fullHeight}
                date={date}
                onChangeDate={set}
                shifts={shifts}
                renderCalendarControls={RenderCalendarControls}
                renderContent={useCallback(
                    () => (
                        <RenderContent
                            initialDate={date}
                            shifts={shifts?.filter(
                                shift => shift.status === "Draft"
                            )}
                            renderControls={() => (
                                <TextButton
                                    icon={
                                        fullHeight ? (
                                            <CloseFullscreenIcon />
                                        ) : (
                                            <OpenInFullIcon />
                                        )
                                    }
                                    onClick={() => setFullHeight(prev => !prev)}
                                />
                            )}
                        />
                    ),
                    [date, fullHeight, shifts]
                )}
                renderFooter={() => (
                    <div
                        css={css`
                            display: grid;
                            grid-template-columns: repeat(3, 1fr);
                            gap: 16px;
                        `}
                    >
                        <FooterIconButton
                            icon={<ArrowBackIosNewIcon />}
                            onClick={() => {
                                if (ids.length > 0) {
                                    const ok =
                                        window.confirm(confirmBackMessage)
                                    if (!ok) {
                                        return
                                    }
                                }

                                navigate("/shift", {
                                    state: {
                                        date: targetDate.format("YYYY-MM-DD")
                                    }
                                })
                            }}
                        >
                            {t("一覧に戻る")}
                        </FooterIconButton>
                        <FooterIconButton icon={<EditCalendarIcon />} disabled>
                            {t("下書きを追加")}
                        </FooterIconButton>
                        <FooterIconButton
                            icon={<UploadIcon />}
                            color="primary"
                            disabled={ids.length === 0}
                            onClick={async () => {
                                const ok = window.confirm(
                                    t(
                                        `${ids.length}件の下書きを提出します。よろしいですか？`
                                    )
                                )
                                if (!ok) {
                                    return
                                }

                                const { error } = await requestSubmitShifts(
                                    token,
                                    {
                                        shiftIds: ids
                                    }
                                )

                                throwAlert(error, {
                                    errorMessage: {
                                        happend: t("シフトの提出に失敗しました")
                                    },
                                    successMessage: t("シフトを提出しました"),
                                    onSuccess: () => {
                                        navigate("/shift", {
                                            state: {
                                                date: targetDate.format(
                                                    "YYYY-MM-DD"
                                                )
                                            }
                                        })
                                    }
                                })
                            }}
                        >
                            {t("提出に進む")}
                        </FooterIconButton>
                    </div>
                )}
            />
        </ShiftSelectContext.Provider>
    )
}
